import type { LICPacketPayloadData, LicPacket } from "@irriga-mais/common";
import dayjs from "dayjs";
import { useCallback, useState } from "react";
import { apiService } from "@/api";

type StatusLicPacket = Omit<LicPacket, "payload_type" | "payload_data"> & {
  payload_type: "status";
  payload_data: LICPacketPayloadData<"status">;
};

export function useRainfallHistory() {
  const [result, setResult] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false); // so para teste
  const [error, setError] = useState<string | null>(null);

  const calculate = useCallback(
    async (device: { id: string; resolutionMm: number }, startDate: Date | string, endDate: Date | string) => {
      const adjustedStart = dayjs(startDate).toISOString();
      const adjustedEnd = dayjs(endDate).toISOString();
      setLoading(true);
      setError(null);
      setResult(null);
      try {
        if (new Date(startDate) >= new Date(endDate)) {
          const message = "A data de início deve ser anterior à data final.";
          throw new Error(message);
        }

        const [firstRaw, lastRaw] = await Promise.all([
          apiService.licPacketHistory.readItemsByLicId({
            id: device.id,
            start_date: adjustedStart,
            end_date: adjustedEnd,
            payload_types: ["status"],
            order: "asc",
            limit: 1,
          }) as Promise<StatusLicPacket[]>,
          apiService.licPacketHistory.readItemsByLicId({
            id: device.id,
            start_date: adjustedStart,
            end_date: adjustedEnd,
            payload_types: ["status"],
            order: "desc",
            limit: 1,
          }) as Promise<StatusLicPacket[]>,
        ]);

        if (!firstRaw || !lastRaw) {
          setResult(null);
          throw new Error("Sem dados de chuva disponíveis para este período.");
        }

        const f = firstRaw.at(0);
        const l = lastRaw.at(0);

        if (!f || !l || !l.payload_data.status.rainfall_global_count) {
          setResult(null);
          throw new Error("Não há registros de chuva no intervalo selecionado.");
        }

        const fTick = Number(f.payload_data.status.rainfall_global_count ?? 0);
        const lTick = Number(l.payload_data.status.rainfall_global_count ?? 0);

        const MAX_COUNT = 2 ** 32 - 1;

        const diff = lTick >= fTick ? lTick - fTick : MAX_COUNT - fTick + lTick;

        setResult(diff * device.resolutionMm);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Erro ao consultar dados de chuva.");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    result,
    loading,
    error,
    calculate,
  };
}
